import { useUsageLimits } from '@/hooks/useUsageLimits';
import { UsageBadge } from './UsageBadge';
import { useLanguage } from '@/contexts';
import { FooterUsageBar } from './FooterUsageBar';
import { FloatingUsageWidget } from './FloatingUsageWidget';
import { UsageIcon } from './UsageIcon';

/**
 * AdaptiveUsageWidget
 * 
 * Displays usage information based on the selected variant:
 * - badge: Shows in sidebar (default) - displays ALL features (top 3)
 * - footer: Shows at bottom of app - displays top 3 features
 * - floating: Shows as floating widget - displays top 5 features
 * - icon: Shows compact version in header - displays only highest usage
 */
interface AdaptiveUsageWidgetProps {
  variant?: 'badge' | 'footer' | 'floating' | 'icon';
}

export function AdaptiveUsageWidget({ variant = 'badge' }: AdaptiveUsageWidgetProps) {
  const { usage, isLoading } = useUsageLimits();
  const { t } = useLanguage();

  // Show skeleton loader while loading (Scenario 8)
  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="skeleton h-6 w-32 bg-gray-200 dark:bg-slate-700 animate-pulse rounded transition-colors duration-300" aria-label={t('usageWidget.loading')}></div>
        <div className="skeleton h-20 w-full bg-gray-200 dark:bg-slate-700 animate-pulse rounded transition-colors duration-300"></div>
      </div>
    );
  }

  // Use mock data for development if backend not available
  const useMockData = (!usage || usage.length === 0) && import.meta.env.DEV;
  
  const effectiveUsage = useMockData ? [
    {
      id: 'mock-1',
      user_id: 'mock-user',
      feature_key: 'api_calls' as const,
      current_usage: 10,
      limit_value: 10,
      reset_period: 'monthly' as const,
      last_reset_at: new Date().toISOString(),
      next_reset_at: new Date().toISOString(),
      last_incremented_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      percentage_used: 100,  // Scenario 3: 100% = Red (highest)
      remaining: 0,
      isUnlimited: false,
      hasCapacity: false,
      isNearLimit: true,
      isAtLimit: true
    },
    {
      id: 'mock-2',
      user_id: 'mock-user',
      feature_key: 'collections' as const,
      current_usage: 8,
      limit_value: 10,
      reset_period: 'monthly' as const,
      last_reset_at: new Date().toISOString(),
      next_reset_at: new Date().toISOString(),
      last_incremented_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      percentage_used: 80,  // Scenario 1: 80% = Yellow
      remaining: 2,
      isUnlimited: false,
      hasCapacity: true,
      isNearLimit: true,
      isAtLimit: false
    },
    {
      id: 'mock-3',
      user_id: 'mock-user',
      feature_key: 'private_prompts' as const,
      current_usage: 5,
      limit_value: 10,
      reset_period: 'monthly' as const,
      last_reset_at: new Date().toISOString(),
      next_reset_at: new Date().toISOString(),
      last_incremented_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      percentage_used: 50,  // Scenario 2: 50% = Green
      remaining: 5,
      isUnlimited: false,
      hasCapacity: true,
      isNearLimit: false,
      isAtLimit: false
    }
  ] : usage;

  // Silently fail if no data available in production
  if (!effectiveUsage || effectiveUsage.length === 0) {
    return null;
  }

  // Sort by highest percentage first (priority display)
  // Filter out unlimited features for cleaner display
  const sorted = [...effectiveUsage]
    .filter(u => !u.isUnlimited)  // Hide unlimited
    .sort((a, b) => (b.percentage_used || 0) - (a.percentage_used || 0));

  // If no limited features or all are unlimited, don't show widget
  if (sorted.length === 0) {
    return null;
  }

  switch (variant) {
    case 'badge':
      // Show ALL features in sidebar (ordered by highest usage first)
      return (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3 uppercase tracking-wide text-start transition-colors duration-300">
            {t('usageWidget.title')}
          </h3>
          <div className="space-y-2">
            {sorted.map((item) => {
              const mockDataProps = useMockData ? {
                current_usage: item.current_usage,
                limit_value: item.limit_value,
                percentage_used: item.percentage_used,
                isUnlimited: item.isUnlimited
              } : undefined;
              
              return (
                <UsageBadge 
                  key={item.feature_key} 
                  featureKey={item.feature_key} 
                  mockData={mockDataProps} 
                />
              );
            })}
          </div>
        </div>
      );
    
    case 'footer':
      // Show top 3 features in footer bar
      return <FooterUsageBar usage={sorted.slice(0, 3)} />;
    
    case 'floating':
      // Show top 5 features in floating expandable widget
      return <FloatingUsageWidget usage={sorted.slice(0, 5)} />;
    
    case 'icon': {
      // Show compact icon version - only highest usage
      const highestUsage = sorted[0];
      const iconMockProps = useMockData ? {
        current_usage: highestUsage.current_usage,
        limit_value: highestUsage.limit_value,
        percentage_used: highestUsage.percentage_used,
        isUnlimited: highestUsage.isUnlimited
      } : undefined;
      return <UsageIcon featureKey={highestUsage.feature_key} mockData={iconMockProps} />;
    }
    
    default:
      // Default to badge behavior (show all features)
      return (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3 uppercase tracking-wide text-start transition-colors duration-300">
            {t('usageWidget.title')}
          </h3>
          <div className="space-y-2">
            {sorted.map((item) => {
              const defaultMockProps = useMockData ? {
                current_usage: item.current_usage,
                limit_value: item.limit_value,
                percentage_used: item.percentage_used,
                isUnlimited: item.isUnlimited
              } : undefined;
              
              return (
                <UsageBadge 
                  key={item.feature_key} 
                  featureKey={item.feature_key} 
                  mockData={defaultMockProps} 
                />
              );
            })}
          </div>
        </div>
      );
  }
}

